import { FC } from 'react';
import { ITemplateResponse } from '@les-sherpas/sherpas-toolbox';
import axios from 'axios';
import server from 'config';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import AdvantageSherpas from '@/components/GiveCourses/AdvantageSherpas';
import HowItWorksTeacher from '@/components/GiveCourses/HowItWorksTeacher/HowItWorksTeacher';
import HowToBecomeSherpas from '@/components/GiveCourses/HowToBecomeSherpas/HowToBecomeSherpas';
import LandingTeacherHero from '@/components/GiveCourses/LandingHeroTeacher/LandingHeroTeacher';
import LandingCarroussel from '@/components/GiveCourses/LandingTeacherCarroussel/LandingTeacherCarroussel';
import {
  AdvantageSherpasDefault,
  HowToBecomeSherpasDefault,
  LandingHeroTeacherDefault,
  LandingTeacherCarrousselDefault,
} from '@/components/GiveCourses/seo.content.default';
import Seo from '@/components/Landing/Seo/Seo';
import LandingSEOParagraphs from '@/components/Landing/Seo/SEOParagraph/LandingSEOParagraph';
import Visio from '@/components/Landing/Visio/Visio';
import SEOFormattedMessage from '@/generic/SEOFormattedMessage/SEOFormattedMessage';
import { getStructuredGiveCourseData } from '@/utils/structuredData';

const DynamicFaq = dynamic(
  () => import('@/components/GiveCourses/TeacherFAQ/TeacherFAQ'),
  {
    ssr: false,
  }
);

const breadcrumbs = [
  {
    label: 'Cours particuliers',
    href: '/',
  },
  {
    label: 'Donner des cours particuliers',
    href: '/',
  },
];

const GiveCourses: FC<ITemplateResponse> = ({ template }) => (
  <>
    <Head>
      <title>{template?.metaTitle}</title>
      <meta name="description" content={template?.metaDescription} />
      <link rel="canonical" href={template?.canonical} />
      <script
        key="structured-data"
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getStructuredGiveCourseData()),
        }}
      />
    </Head>
    <LandingTeacherHero
      title={template?.h1Content}
      backgroundImage={LandingHeroTeacherDefault.defaultBackgroundUrl}
      subtitle={LandingHeroTeacherDefault.defaultSubtitle}
    >
      <LandingTeacherHero.Breadcrumb>
        {breadcrumbs.map((breadcrumb, index) => (
          <LandingTeacherHero.Breadcrumb.Item
            content={`${index + 1}`}
            label={breadcrumb.label}
            href={breadcrumb.href}
            isLastElement={index === breadcrumbs.length - 1}
            key={`breadcrumb-${index}`}
          />
        ))}
      </LandingTeacherHero.Breadcrumb>
    </LandingTeacherHero>
    <AdvantageSherpas
      title={AdvantageSherpasDefault.defaultTitle}
      subtitle={AdvantageSherpasDefault.defaultSubtitle}
    />
    <HowItWorksTeacher />
    <Visio isTeacher />
    <LandingCarroussel
      title={LandingTeacherCarrousselDefault.defaultTitle}
      subtitle={LandingTeacherCarrousselDefault.defaultSubtitle}
    />
    <HowToBecomeSherpas
      title={HowToBecomeSherpasDefault.defaultTitle}
      subtitle={HowToBecomeSherpasDefault.defaultSubtitle}
      featureLabel={HowToBecomeSherpasDefault.defaultLabel}
      featureBackground={HowToBecomeSherpasDefault.defaultBackgroundUrl}
      howToSections={HowToBecomeSherpasDefault.defaultSections}
    />
    <DynamicFaq />
    <Seo
      subjects={template?.subjects}
      levels={template?.levels}
      cities={template?.cities}
    />
    {template?.seoParagraphs?.length !== 0 && (
      <LandingSEOParagraphs
        title={
          <SEOFormattedMessage
            id="landing.courses.seo.seoSection.title"
            defaultMessage={template?.h2Content10}
          />
        }
        sectionData={template?.seoParagraphs}
      />
    )}
  </>
);

export const getStaticProps: GetStaticProps = async () => {
  const { data } = await axios.get<ITemplateResponse>(
    `${server}template/donner-cours-particuliers`,
    { validateStatus: (status) => status === 200 }
  );

  return {
    props: data,
    revalidate: 3600,
  };
};

export default GiveCourses;
